import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, patch, post } from "@/lib/api";
import { FieldError } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const blank = { full_name: "", company: "", phone: "", create_account: false, email: "" };
const FIELDS = [["full_name", "Name *"], ["company", "Company *"], ["phone", "Telephone number *", "tel"]];

export const OfficerDialog = ({ open, onOpenChange, officer, onSaved, allowAccountCreation = false }) => {
  const [f, setF] = useState(blank);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setF(officer ? Object.fromEntries(Object.keys(blank).map((k) => [k, officer[k] ?? blank[k]])) : blank);
      setErrors({});
    }
  }, [open, officer]);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (f.full_name.trim().length < 2) errs.full_name = "Enter the officer's name";
    if (f.company.trim().length < 2) errs.company = "Enter the company";
    if (!/^[0-9+()\-\s]{6,40}$/.test(f.phone.trim())) errs.phone = "Enter a valid telephone number";
    if (!officer && allowAccountCreation && f.create_account && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) errs.email = "Enter a valid email address";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    const data = Object.fromEntries(Object.entries(f).filter(([k]) => ["full_name", "company", "phone"].includes(k)).map(([k, v]) => [k, v.trim()]));
    if (!officer && allowAccountCreation && f.create_account) {
      data.create_account = true;
      data.email = f.email.trim().toLowerCase();
    }
    try {
      const saved = await (officer ? patch(`/officers/${officer.id}`, data) : post("/officers", data));
      toast.success(officer ? "Officer updated" : saved.account_user ? "Officer added and account invitation sent" : "Officer added");
      onSaved(saved);
      onOpenChange(false);
    } catch (err) {
      setErrors({ form: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg" data-testid="officer-dialog">
        <DialogHeader><DialogTitle className="font-display">{officer ? "Edit officer" : "Add officer"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <div className="space-y-4">
            {FIELDS.map(([k, label, type]) => (
              <div key={k}>
                <Label htmlFor={`of-${k}`}>{label}</Label>
                <Input id={`of-${k}`} type={type || "text"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1.5" data-testid={`officer-${k}-input`} />
                <FieldError message={errors[k]} testId={`officer-${k}-error`} />
              </div>
            ))}
          </div>
          {!officer && allowAccountCreation && (
            <div className="space-y-3 rounded-md border border-slate-200 p-3">
              <label className="flex cursor-pointer items-center gap-3 text-sm font-medium text-slate-800">
                <Checkbox checked={f.create_account} onCheckedChange={(checked) => setF({ ...f, create_account: !!checked })} data-testid="officer-create-account-checkbox" />
                Create an Agent account for this officer
              </label>
              {f.create_account && <div>
                <Label htmlFor="of-email">Account email *</Label>
                <Input id="of-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" data-testid="officer-email-input" />
                <FieldError message={errors.email} testId="officer-email-error" />
              </div>}
            </div>
          )}
          {errors.form && <p role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-rose-700" data-testid="officer-form-error">{errors.form}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="officer-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="officer-save-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save officer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
